import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import {
  DashboardBriefDto,
  DashboardCreatePayload,
  DashboardDto,
  DashboardMutatePayload,
  MutationOptions,
  MutationResult,
  QueryResult,
} from '@thingslog/repositories';
import { QueryKeys } from '../enums/QueryKeys';
import { QueryOptions } from '@thingslog/repositories';
import { buildDashboardUrl } from './dashboardUrlUtils';

export function createDashboardQueryClient(axios: AxiosInstance): DashboardQueryClient {
  return new DashboardQueryClientImp(axios);
}

class DashboardQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public useDashboards = (
    companyId?: string | number | null,
    options?: QueryOptions<DashboardBriefDto[]>
  ): QueryResult<DashboardBriefDto[]> => {
    return useQuery<DashboardBriefDto[], AxiosError>(
      [QueryKeys.GetDashboards, companyId],
      () => this.getDashboards(companyId),
      options
    );
  };

  public useDashboard = (
    id: string,
    companyId?: string | number | null,
    options?: QueryOptions<DashboardDto>
  ): QueryResult<DashboardDto> => {
    return useQuery<DashboardDto, AxiosError>(
      [QueryKeys.GetDashboard, id, companyId],
      () => this.getDashboard(id, companyId),
      options
    );
  };

  public useCreateDashboard = (
    options?: MutationOptions<
      DashboardDto,
      DashboardCreatePayload & { companyId?: string | number | null }
    >
  ): MutationResult<
    DashboardDto,
    DashboardCreatePayload & { companyId?: string | number | null }
  > => {
    return useMutation<
      DashboardDto,
      AxiosError,
      DashboardCreatePayload & { companyId?: string | number | null }
    >(
      [QueryKeys.CreateDashboard],
      (payload: DashboardCreatePayload & { companyId?: string | number | null }) =>
        this.createDashboard(payload),
      options
    );
  };

  public useUpdateDashboard = (
    options?: MutationOptions<
      DashboardDto,
      { id: string; body: DashboardMutatePayload; companyId?: string | number | null }
    >
  ): MutationResult<
    DashboardDto,
    { id: string; body: DashboardMutatePayload; companyId?: string | number | null }
  > => {
    return useMutation<
      DashboardDto,
      AxiosError,
      { id: string; body: DashboardMutatePayload; companyId?: string | number | null }
    >(
      [QueryKeys.UpdateDashboard],
      ({
        id,
        body,
        companyId,
      }: {
        id: string;
        body: DashboardMutatePayload;
        companyId?: string | number | null;
      }) => this.updateDashboard(id, body, companyId),
      options
    );
  };

  public useDeleteDashboard = (
    options?: MutationOptions<boolean, string | { id: string; companyId?: string | number | null }>
  ): MutationResult<boolean, string | { id: string; companyId?: string | number | null }> => {
    return useMutation<
      boolean,
      AxiosError,
      string | { id: string; companyId?: string | number | null }
    >(
      [QueryKeys.DeleteDashboard],
      (idOrPayload: string | { id: string; companyId?: string | number | null }) => {
        if (typeof idOrPayload === 'string') {
          return this.deleteDashboard(idOrPayload);
        }
        return this.deleteDashboard(idOrPayload.id, idOrPayload.companyId);
      },
      options
    );
  };

  public useSetDefaultDashboard = (
    options?: MutationOptions<DashboardDto, { id: string; companyId?: string | number | null }>
  ): MutationResult<DashboardDto, { id: string; companyId?: string | number | null }> => {
    return useMutation<
      DashboardDto,
      AxiosError,
      { id: string; companyId?: string | number | null }
    >(
      [QueryKeys.SetDefaultDashboard],
      ({ id, companyId }: { id: string; companyId?: string | number | null }) =>
        this.setDefaultDashboard(id, companyId),
      options
    );
  };

  public useSetFavoriteDashboard = (
    options?: MutationOptions<
      DashboardDto,
      { id: string; isFavorite: boolean; companyId?: string | number | null }
    >
  ): MutationResult<
    DashboardDto,
    { id: string; isFavorite: boolean; companyId?: string | number | null }
  > => {
    return useMutation<
      DashboardDto,
      AxiosError,
      { id: string; isFavorite: boolean; companyId?: string | number | null }
    >(
      [QueryKeys.SetFavoriteDashboard],
      ({
        id,
        isFavorite,
        companyId,
      }: {
        id: string;
        isFavorite: boolean;
        companyId?: string | number | null;
      }) => this.setFavoriteDashboard(id, isFavorite, companyId),
      options
    );
  };

  private getDashboards = async (
    companyId?: string | number | null
  ): Promise<DashboardBriefDto[]> => {
    return await this.axios
      .get(buildDashboardUrl('/dashboard/api/dashboards/list'), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<DashboardBriefDto[]>) => response.data);
  };

  private getDashboard = async (
    id: string,
    companyId?: string | number | null
  ): Promise<DashboardDto> => {
    return await this.axios
      .get(buildDashboardUrl(`/dashboard/api/dashboards/${id}`), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<DashboardDto>) => response.data);
  };

  private createDashboard = async (
    payload: DashboardCreatePayload & { companyId?: string | number | null }
  ): Promise<DashboardDto> => {
    const { companyId, ...body } = payload;
    return await this.axios
      .post(buildDashboardUrl('/dashboard/api/dashboards'), body, {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<DashboardDto>) => response.data);
  };

  private updateDashboard = async (
    id: string,
    body: DashboardMutatePayload,
    companyId?: string | number | null
  ): Promise<DashboardDto> => {
    return await this.axios
      .put(buildDashboardUrl(`/dashboard/api/dashboards/${id}`), body, {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<DashboardDto>) => response.data);
  };

  private deleteDashboard = async (
    id: string,
    companyId?: string | number | null
  ): Promise<boolean> => {
    return await this.axios
      .delete(buildDashboardUrl(`/dashboard/api/dashboards/${id}`), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<boolean>) => response.data);
  };

  private setDefaultDashboard = async (
    id: string,
    companyId?: string | number | null
  ): Promise<DashboardDto> => {
    return await this.axios
      .put(buildDashboardUrl(`/dashboard/api/dashboards/${id}/default`), null, {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<DashboardDto>) => response.data);
  };

  private setFavoriteDashboard = async (
    id: string,
    isFavorite: boolean,
    companyId?: string | number | null
  ): Promise<DashboardDto> => {
    return await this.axios
      .put(buildDashboardUrl(`/dashboard/api/dashboards/${id}/favorite`), null, {
        params: companyId != null ? { companyId, isFavorite } : { isFavorite },
      })
      .then((response: AxiosResponse<DashboardDto>) => response.data);
  };
}

export interface DashboardQueryClient {
  useDashboards: (
    companyId?: string | number | null,
    options?: QueryOptions<DashboardBriefDto[]>
  ) => QueryResult<DashboardBriefDto[]>;
  useDashboard: (
    id: string,
    companyId?: string | number | null,
    options?: QueryOptions<DashboardDto>
  ) => QueryResult<DashboardDto>;
  useCreateDashboard: (
    options?: MutationOptions<
      DashboardDto,
      DashboardCreatePayload & { companyId?: string | number | null }
    >
  ) => MutationResult<
    DashboardDto,
    DashboardCreatePayload & { companyId?: string | number | null }
  >;
  useUpdateDashboard: (
    options?: MutationOptions<
      DashboardDto,
      { id: string; body: DashboardMutatePayload; companyId?: string | number | null }
    >
  ) => MutationResult<
    DashboardDto,
    { id: string; body: DashboardMutatePayload; companyId?: string | number | null }
  >;
  useDeleteDashboard: (
    options?: MutationOptions<boolean, string | { id: string; companyId?: string | number | null }>
  ) => MutationResult<boolean, string | { id: string; companyId?: string | number | null }>;
  useSetDefaultDashboard: (
    options?: MutationOptions<DashboardDto, { id: string; companyId?: string | number | null }>
  ) => MutationResult<DashboardDto, { id: string; companyId?: string | number | null }>;
  useSetFavoriteDashboard: (
    options?: MutationOptions<
      DashboardDto,
      { id: string; isFavorite: boolean; companyId?: string | number | null }
    >
  ) => MutationResult<
    DashboardDto,
    { id: string; isFavorite: boolean; companyId?: string | number | null }
  >;
}
